import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';

const buttonVariants = cva(
  'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-200 press-scale focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-foreground text-background shadow-ios hover:bg-foreground/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-ios hover:bg-destructive/90',
        outline:
          'border border-input bg-background shadow-ios hover:bg-muted hover:text-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-muted hover:text-foreground',
        link: 'text-foreground underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 px-5 py-2.5',
        sm: 'h-10 rounded-lg px-4',
        lg: 'h-12 rounded-xl px-8',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const Button = React.forwardRef(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      withArrow = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          disabled={disabled}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    const isDisabled = disabled || loading;

    return (
      <button
        // HTML defaults a <button> to type="submit", which quietly turns every
        // unmarked Button inside a <form> into a submit button — Cancel, a
        // confirm trigger, a secondary action. Wrapping anything in a <form>
        // then fires the wrong thing on Return. Default to the inert type; the
        // one button that submits says type="submit" and, being after the
        // spread, still wins.
        type="button"
        className={cn(buttonVariants({ variant, size, className }), 'group')}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          withArrow ? (
            <>
              <span className="contents [&>svg]:hidden">{children}</span>
              <Spinner size="sm" />
            </>
          ) : (
            <>
              <Spinner size="sm" />
              <span className="contents [&>svg]:hidden">{children}</span>
            </>
          )
        ) : (
          <>
            {children}
            {withArrow && (
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            )}
          </>
        )}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
